import { createSignal, createEffect, Show } from 'solid-js';
import { A } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useTenantPermission } from '../hooks/tenantPermissions';
import { useUserContext } from '../components/UserContextProvider';
import { Tenant, TenantSettings as TenantSettingsModel, TenantRole } from '../models/Tenant';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import StateDisplay from '../components/StateDisplay';
import { useTranslation } from '../hooks/useTranslation';
import styles from './TenantSettings.module.css';

export default function TenantSettings() {
    const api = useApi();
    const toast = useToast();
    const { userTenant } = useUserContext();
    const { hasRole } = useTenantPermission();
    const { t } = useTranslation();

    const tenantId = () => userTenant()?.tenantId ?? null;

    const [loading, setLoading] = createSignal(true);
    const [tenant, setTenant] = createSignal<Tenant | null>(null);
    const [, setSettings] = createSignal<TenantSettingsModel | null>(null);
    const [description, setDescription] = createSignal('');
    const [defaultProfitMargin, setDefaultProfitMargin] = createSignal(0.2);
    const [maxMembers, setMaxMembers] = createSignal(50);
    const [allowMemberInvites, setAllowMemberInvites] = createSignal(true);
    const [contributeToGlobalRegistry, setContributeToGlobalRegistry] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    createEffect(() => {
        const id = tenantId();
        if (!id) {
            setTenant(null);
            setSettings(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        void (async () => {
            try {
                const res = await api.tenant.getTenant(id);
                if (isOk(res.data)) setTenant(res.data.value as Tenant);

                const sres = await api.tenant.getSettings(id);
                if (isOk(sres.data)) {
                    const s = sres.data.value as TenantSettingsModel;
                    setSettings(s);
                    setDescription(s.description ?? '');
                    setDefaultProfitMargin(s.defaultProfitMargin ?? 0.2);
                    setMaxMembers(s.maxMembers ?? 50);
                    setAllowMemberInvites(s.allowMemberInvites ?? true);
                    setContributeToGlobalRegistry(s.contributeToGlobalRegistry ?? true);
                }
            } finally {
                setLoading(false);
            }
        })();
    });

    async function saveSettings() {
        const id = tenantId();
        if (!id) return;
        setSaving(true);
        try {
            const res = await api.tenant.updateSettings(id, {
                defaultProfitMargin: defaultProfitMargin(),
                description: description(),
                maxMembers: maxMembers(),
                allowMemberInvites: allowMemberInvites(),
                contributeToGlobalRegistry: contributeToGlobalRegistry()
            });
            if (isOk(res.data)) toast?.info(t('TENANT_SETTINGS_SAVED'));
        } finally {
            setSaving(false);
        }
    }

    function roleChipClass(role: TenantRole) {
        switch (role) {
            case TenantRole.Owner: return styles.roleChipOwner;
            case TenantRole.Admin: return styles.roleChipAdmin;
            default: return styles.roleChipMember;
        }
    }

    const canEdit = () => hasRole(TenantRole.Admin);

    return (
        <div class={styles.container}>
            <Show
                when={tenantId()}
                fallback={
                    <div class={styles.emptyHero}>
                        <i class="fa-solid fa-users-slash"></i>
                        <h2><Text val="TENANT_NO_SELECTION" /></h2>
                        <p><Text val="TENANT_CREATE_HINT" /></p>
                        <A href="/tenant/new" class="btn btn-primary">
                            <i class="fa-solid fa-plus"></i>
                            <Text val="TENANT_CREATE" />
                        </A>
                    </div>
                }
            >
                <Show when={loading()}>
                    <StateDisplay loading />
                </Show>
                <Show when={!loading() && tenant()}>
                    <div class={styles.header}>
                        <div class={styles.avatar}>
                            <i class="fa-solid fa-building"></i>
                        </div>
                        <div class={styles.titleText}>
                            <h1>{tenant()?.name}</h1>
                            <div class={styles.titleMeta}>
                                <span class={styles.slug}>@{tenant()?.slug}</span>
                                <span class={styles.slugDot}>·</span>
                                <span class={`${styles.roleChip} ${roleChipClass(userTenant()?.role ?? TenantRole.Member)}`}>
                                    <Show when={(userTenant()?.role ?? TenantRole.Member) === TenantRole.Owner}><i class="fa-solid fa-crown"></i></Show>
                                    <Show when={(userTenant()?.role ?? TenantRole.Member) === TenantRole.Admin}><i class="fa-solid fa-user-shield"></i></Show>
                                    <Show when={(userTenant()?.role ?? TenantRole.Member) === TenantRole.Member}><i class="fa-solid fa-user"></i></Show>
                                    {TenantRole[userTenant()?.role ?? TenantRole.Member]}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Show when={canEdit()}>
                        <div class={styles.sectionHeader}>
                            <h2><Text val="TENANT_INFO" /></h2>
                        </div>
                        <div class={styles.card}>
                            <div class={styles.field}>
                                <label class={styles.fieldLabel}><Text val="TENANT_DESCRIPTION" /></label>
                                <textarea
                                    class={styles.textarea}
                                    value={description()}
                                    onInput={e => setDescription(e.currentTarget.value)}
                                    placeholder={t('TENANT_DESCRIPTION_PLACEHOLDER')}
                                />
                            </div>
                        </div>

                        <div class={styles.sectionHeader}>
                            <h2><Text val="SETTINGS" /></h2>
                        </div>
                        <div class={styles.card}>
                            <div class={styles.row}>
                                <div class={styles.field}>
                                    <label class={styles.fieldLabel}><Text val="TENANT_PROFIT_MARGIN" /></label>
                                    <input
                                        type="number"
                                        class={styles.input}
                                        value={defaultProfitMargin()}
                                        step="0.01"
                                        min="0"
                                        max="1"
                                        onInput={e => setDefaultProfitMargin(parseFloat(e.currentTarget.value))}
                                    />
                                    <span class={styles.fieldHelp}>0.0-1.0</span>
                                </div>
                                <div class={styles.field}>
                                    <label class={styles.fieldLabel}><Text val="TENANT_MAX_MEMBERS" /></label>
                                    <input
                                        type="number"
                                        class={styles.input}
                                        value={maxMembers()}
                                        min="1"
                                        onInput={e => setMaxMembers(parseInt(e.currentTarget.value))}
                                    />
                                </div>
                            </div>

                            <div class={styles.toggleRow}>
                                <div class={styles.toggleInfo}>
                                    <span class={styles.toggleLabel}><Text val="TENANT_ALLOW_INVITES" /></span>
                                </div>
                                <label class={styles.switch}>
                                    <input
                                        type="checkbox"
                                        checked={allowMemberInvites()}
                                        onChange={e => setAllowMemberInvites(e.currentTarget.checked)}
                                    />
                                    <span class={styles.switchSlider}></span>
                                </label>
                            </div>

                            <div class={styles.toggleRow}>
                                <div class={styles.toggleInfo}>
                                    <span class={styles.toggleLabel}><Text val="TENANT_CONTRIBUTE_REGISTRY" /></span>
                                    <span class={styles.fieldHelp}><Text val="TENANT_CONTRIBUTE_REGISTRY_HELP" /></span>
                                </div>
                                <label class={styles.switch}>
                                    <input
                                        type="checkbox"
                                        checked={contributeToGlobalRegistry()}
                                        onChange={e => setContributeToGlobalRegistry(e.currentTarget.checked)}
                                    />
                                    <span class={styles.switchSlider}></span>
                                </label>
                            </div>

                            <div class={styles.actions}>
                                <button class="btn btn-primary" onClick={saveSettings} disabled={saving()}>
                                    <Show when={saving()} fallback={<i class="fa-solid fa-floppy-disk" />}>
                                        <i class="fa-solid fa-spinner fa-spin" />
                                    </Show>
                                    {saving() ? t('TENANT_SAVING') : t('TENANT_SAVE_SETTINGS')}
                                </button>
                            </div>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
